import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import type { ListParams, ListResult } from '../../../api/listApi.types'

export interface UseListQueryOptions<T>
  extends Omit<UseQueryOptions<ListResult<T>>, 'queryKey' | 'queryFn'> {
  queryKey: unknown[]
  params: ListParams
  queryFn: (params: ListParams) => Promise<ListResult<T>>
}

export interface UseListQueryResult<T> {
  data: T[]
  total: number
  isLoading: boolean
  isFetching: boolean
  /** True when stale data from a prior query key is shown while new params load. */
  isPlaceholderData: boolean
  error: Error | null
}

/** Params that define which rows are returned (excludes page — pagination may keep stale rows). */
export function listQueryDataSignature(params: ListParams): string {
  return JSON.stringify({
    limit: params.limit,
    search: params.search ?? '',
    sort_by: params.sort_by,
    order: params.order,
    filters: params.filters ?? {},
  })
}

export function useListQuery<T>({
  queryKey,
  params,
  queryFn,
  ...queryOptions
}: UseListQueryOptions<T>): UseListQueryResult<T> {
  const dataSignature = listQueryDataSignature(params)

  const result = useQuery({
    queryKey: [...queryKey, params],
    queryFn: () => queryFn(params),
    // Always keep previous rows visible while the next page of results loads.
    // This prevents skeleton flashing when filters/search/sort change — instead
    // the stale rows stay visible under the dimming overlay until new data arrives.
    // isLoading (skeletons) is now only true on the very first fetch with no cached data.
    placeholderData: (previousData) => previousData ?? undefined,
    ...queryOptions,
  })

  return {
    data: result.data?.data ?? [],
    total: result.data?.total ?? 0,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isPlaceholderData: result.isPlaceholderData,
    error:
      result.error instanceof Error
        ? result.error
        : result.error
          ? new Error(String(result.error))
          : null,
  }
}
